import { clientDb } from '../firebase';
import { iKmlFile } from './kml-db';
import { iFenceDetails, ItemType, iIcon } from '../interfaces';

export interface iShareLinkFence {
    id: string;
    name: string;
    color: string;
    region: any;
    icon: boolean | iIcon;
}

export interface iShareLinkConfig {
    kml: { [kmlId: string]: iKmlFile };
    geofences: { [fenceId: string]: iShareLinkFence };
}

const shareLinkConfigDb = () => clientDb().child('share-link-config');

/**
 * Get or initialize share link config for a tag
 */
export const getShareLinkConfig = async (tagId: string): Promise<iShareLinkConfig> => {
    try {
        const snapshot = await shareLinkConfigDb().child(tagId).once('value');
        const config = snapshot.val();

        return config || { kml: {}, geofences: {} };
    } catch (err) {
        console.error('Failed to load share-link config:', err);
        return { kml: {}, geofences: {} };
    }
};

/**
 * Set KML file in share link config
 */
export const setShareLinkKml = async (tagId: string, kmlId: string, file: iKmlFile | null): Promise<void> => {
    try {
        if (file === null) {
            // Delete the KML
            await shareLinkConfigDb().child(`${tagId}/kml/${kmlId}`).remove();
        } else {
            // Set or update the KML
            await shareLinkConfigDb().child(`${tagId}/kml/${kmlId}`).set(file);
        }
    } catch (err) {
        console.error('Failed to set share-link KML:', err);
        throw err;
    }
};

/**
 * Set geofence in share link config
 */
export const setShareLinkGeofence = async (tagId: string, fenceId: string, fence: iShareLinkFence | null): Promise<void> => {
    try {
        if (fence === null) {
            // Delete the geofence
            await shareLinkConfigDb().child(`${tagId}/geofences/${fenceId}`).remove();
        } else {
            // Set or update the geofence
            await shareLinkConfigDb().child(`${tagId}/geofences/${fenceId}`).set(fence);
        }
    } catch (err) {
        console.error('Failed to set share-link geofence:', err);
        throw err;
    }
};

/**
 * Get all geofences available by querying all fence instances from Firebase
 */
export const getAllFencesFlat = async (): Promise<{ [fenceId: string]: iFenceDetails }> => {
    try {
        const allFences: { [fenceId: string]: iFenceDetails } = {};

        // Query all fence instances for all item types
        const snapshot = await clientDb().child('fences/fence-instances').once('value');
        const fenceInstances = snapshot.val();

        if (fenceInstances) {
            // Flatten the nested structure: fences/fence-instances/{type}/{itemId}/{fenceId}/{properties}
            Object.values(ItemType).forEach((type: any) => {
                const typeFences = fenceInstances[type];
                if (typeFences) {
                    Object.values(typeFences).forEach((itemFences: any) => {
                        if (itemFences && typeof itemFences === 'object') {
                            Object.entries(itemFences).forEach(([fenceId, fenceData]: [string, any]) => {
                                if (fenceData && fenceData.id) {
                                    allFences[fenceId] = fenceData;
                                }
                            });
                        }
                    });
                }
            });
        }

        return allFences;
    } catch (err) {
        console.error('Failed to get all fences:', err);
        return {};
    }
};
